import React, { useState } from 'react';
import Icon from '../components/Icon';

const ProvablyFairPage = () => {
  const [activeTab, setActiveTab] = useState('coinflip');
  const [serverSeed, setServerSeed] = useState('');
  const [clientSeed, setClientSeed] = useState('');
  const [nonce, setNonce] = useState('');
  const [hash, setHash] = useState('');
  const [verificationResult, setVerificationResult] = useState('');

  const verifyGame = () => {
    // This is a simplified verification example
    // In a real implementation, this would involve cryptographic hashing
    if (serverSeed && clientSeed && nonce) {
      // Normally you'd combine serverSeed + clientSeed + nonce and hash it
      // For demonstration purposes, we'll just show how it works conceptually
      const combined = `${serverSeed}${clientSeed}${nonce}`;
      const calculatedHash = btoa(combined).substring(0, 32); // Simple base64 encoding for demo
      
      if (calculatedHash === hash) {
        setVerificationResult('VERIFIED_OK|Verification successful! The game result is provably fair.');
      } else {
        setVerificationResult('VERIFIED_FAIL|Verification failed! The game result may have been tampered with.');
      }
    }
  };

  return (
    <div className="provably-fair-page">
      <div className="page-header">
        <h1>Provably Fair</h1>
      </div>

      <div className="fair-explanation">
        <p>
          Our Provably Fair system ensures that all game outcomes are verifiable and unbiased. 
          You can verify that game results are not manipulated by the server.
        </p>
      </div>

      <div className="fair-tabs">
        <button 
          className={`tab-button ${activeTab === 'coinflip' ? 'active' : ''}`}
          onClick={() => setActiveTab('coinflip')}
        >
          Coinflip
        </button>
        <button 
          className={`tab-button ${activeTab === 'blackjack' ? 'active' : ''}`}
          onClick={() => setActiveTab('blackjack')}
        >
          Blackjack
        </button>
        <button 
          className={`tab-button ${activeTab === 'how-it-works' ? 'active' : ''}`}
          onClick={() => setActiveTab('how-it-works')}
        >
          How It Works
        </button>
        <button 
          className={`tab-button ${activeTab === 'verify' ? 'active' : ''}`}
          onClick={() => setActiveTab('verify')}
        >
          Verify Game
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'coinflip' && (
          <div className="fair-content">
            <h3>Coinflip Provably Fair</h3>
            <p>
              Our coinflip system uses three components to ensure fairness:
            </p>
            <ul>
              <li><strong>Server Seed:</strong> A secret value generated by our server</li>
              <li><strong>Client Seed:</strong> A value provided by your browser</li>
              <li><strong>Nonce:</strong> A unique number incremented with each game</li>
            </ul>
            <p>
              These three values are combined and hashed using SHA-256 to determine the outcome 
              before the game begins. Since the server seed is hashed before you place your bet, 
              the result cannot be manipulated.
            </p>
          </div>
        )}

        {activeTab === 'blackjack' && (
          <div className="fair-content">
            <h3>Blackjack Provably Fair</h3>
            <p>
              Our blackjack system ensures a fair deck shuffle using provable fairness:
            </p>
            <ul>
              <li><strong>Server Seed:</strong> A secret value generated by our server</li>
              <li><strong>Client Seed:</strong> A value provided by your browser</li>
              <li><strong>Nonce:</strong> A unique number incremented with each game</li>
            </ul>
            <p>
              The deck is shuffled using a cryptographically secure algorithm that combines 
              these values, ensuring that neither the server nor the player can predict or 
              manipulate the card order.
            </p>
          </div>
        )}

        {activeTab === 'how-it-works' && (
          <div className="fair-content">
            <h3>How Provably Fair Works</h3>
            <ol>
              <li>
                <strong>Before you play:</strong> The server generates a random Server Seed 
                and provides you with its hash (this is the Commitment).
              </li>
              <li>
                <strong>During gameplay:</strong> The Client Seed (from your browser) and 
                a Nonce (game counter) are combined with the Server Seed to determine the outcome.
              </li>
              <li>
                <strong>After gameplay:</strong> The server reveals the Server Seed. You can 
                verify that it matches the hash provided before the game.
              </li>
              <li>
                <strong>Verification:</strong> Anyone can verify that the outcome was 
                determined fairly using the three values.
              </li>
            </ol>
            <div className="fair-diagram">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-desc">Server generates Server Seed</div>
              </div>
              <div className="arrow">→</div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-desc">Server provides Hash(Server Seed)</div>
              </div>
              <div className="arrow">→</div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-desc">Game is played using all 3 values</div>
              </div>
              <div className="arrow">→</div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-desc">Server reveals Server Seed</div>
              </div>
              <div className="arrow">→</div>
              <div className="step">
                <div className="step-number">5</div>
                <div className="step-desc">Verify: Hash(Server Seed) matches commitment</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'verify' && (
          <div className="verify-section">
            <h3>Verify a Previous Game</h3>
            <div className="verification-form">
              <div className="form-group">
                <label className="form-label">Server Seed</label>
                <input
                  type="text"
                  value={serverSeed}
                  onChange={(e) => setServerSeed(e.target.value)}
                  className="form-control"
                  placeholder="Enter server seed from the game"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Client Seed</label>
                <input
                  type="text"
                  value={clientSeed}
                  onChange={(e) => setClientSeed(e.target.value)}
                  className="form-control"
                  placeholder="Enter client seed you provided"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Nonce</label>
                <input
                  type="text"
                  value={nonce}
                  onChange={(e) => setNonce(e.target.value)}
                  className="form-control"
                  placeholder="Enter nonce value"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Expected Hash</label>
                <input
                  type="text"
                  value={hash}
                  onChange={(e) => setHash(e.target.value)}
                  className="form-control"
                  placeholder="Enter the hash committed before the game"
                />
              </div>
              <button 
                className="btn btn-primary" 
                onClick={verifyGame}
              >
                Verify Game
              </button>
            </div>
            {verificationResult && (() => {
              const ok = verificationResult.startsWith('VERIFIED_OK|');
              const text = verificationResult.split('|').slice(1).join('|');
              return (
                <div className={`verification-result ${ok ? 'success' : 'error'}`}>
                  <Icon name={ok ? 'check' : 'close'} size={16} /> {text}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProvablyFairPage;